// Entity-resolution GROUP review API (hidden system-node model).
//
// A "group" is a neutral `__ResolutionGroup__` hub that collects candidate-
// duplicate entities via `member -[:IN_RESOLUTION_GROUP]-> hub` edges. Unlike
// the older star clusters, the hub is never one of the real entities, so there
// is no canonical to choose at suggestion time — the analyst picks the survivor
// only at merge time. Produced by resolution_group.py.
import { apiClient } from './client'
import type { EntityResolutionJob } from './entity-resolution'

export interface GroupMember {
  /** Neo4j elementId. */
  id: string
  name: string
  /** Non-internal labels (those not starting with `__`). */
  labels: string[]
  properties: Record<string, unknown>
  /** Count of graph relationships — used as a "richness" hint to default the survivor. */
  degree: number
  /**
   * Similarity (0..1) to the cluster representative, computed and gated in the
   * backend at grouping time (SP-362). Always ≥ the configured floor for
   * name-based groups; `null` for the representative-less property/legacy groups.
   * The UI renders this verbatim and never recomputes its own similarity.
   */
  similarityScore: number | null
}

export interface IdentityAssessment {
  status: 'conflict' | 'review' | 'supported' | 'unknown'
  conflicts: string[]
  reviewFields: string[]
  matchedFields: string[]
}

export interface ResolutionGroup {
  groupId: string
  /** Why the members were grouped, e.g. "name", "on_demand:address". */
  reason: string | null
  /** The member every score is measured against and the default survivor (the
   * richest node, chosen in the backend). `null` for legacy/property groups. */
  representativeId: string | null
  /** For on-demand "Find duplicates" groups: the property that was scanned.
   * `null` for the automatic name path. */
  searchProperty: string | null
  /** The 0..1 tolerance the analyst chose for that scan. `null` for the name
   * path and for legacy on-demand groups created before it was recorded. */
  searchThreshold: number | null
  members: GroupMember[]
  memberCount: number
  identityAssessment?: IdentityAssessment
}

export interface GroupPage {
  groups: ResolutionGroup[]
  total: number
}

export interface GroupPendingCount {
  candidates: number
  groups: number
}

export interface GroupMergeRequest {
  group_id: string
  /** The entity that survives; every id in `member_ids` folds into it. */
  survivor_id: string
  member_ids: string[]
  similarity_score?: number | null
}

export interface GroupRejectMemberRequest {
  group_id: string
  member_id: string
}

export interface GroupDismissRequest {
  group_id: string
}

export interface FindDuplicatesRequest {
  label: string
  property: string
  threshold: number
}

export interface FindDuplicatesResult {
  groups_created: number
  members_attached: number
  clusters: number
  /** Prior suggestions for this entity type + property dropped before the
   * re-run, so identical groups replace rather than stack. */
  cleared?: number
}

/** On-demand duplicate scan runs as a polled background job, like recalculate. */
export interface FindDuplicatesJob extends EntityResolutionJob {
  result: FindDuplicatesResult | null
}

export interface MergeExactResult {
  groups_total: number
  groups_processed: number
  merged_groups: number
  merged_members: number
  skipped_groups: number
  failed_groups: number
  last_error: string | null
}

/** Bulk merge of fully-exact clusters runs as a polled background job.
 * `progress` carries interim counts while the job is still running. */
export interface MergeExactJob extends EntityResolutionJob {
  result: MergeExactResult | null
  progress?: MergeExactResult | null
}

export const groupReviewApi = {
  /** Hidden review groups for a case, biggest first. */
  list: (caseId: string, options?: { limit?: number; offset?: number }) => {
    const params: Record<string, number> = {}
    if (options?.limit !== undefined) params.limit = options.limit
    if (options?.offset !== undefined) params.offset = options.offset
    return apiClient.get<GroupPage>(`/v1/cases/${caseId}/entity-resolution/groups`, {
      params: Object.keys(params).length ? params : undefined,
    })
  },

  /** Candidates + groups still awaiting resolution — the nav badge. */
  pendingCount: (caseId: string) =>
    apiClient.get<GroupPendingCount>(`/v1/cases/${caseId}/entity-resolution/groups/pending-count`),

  /** Fold the chosen members into the survivor, then prune the hub (audited). */
  merge: (caseId: string, body: GroupMergeRequest) =>
    apiClient.post<void>(`/v1/cases/${caseId}/entity-resolution/groups/merge`, body),

  /** Drop a member from a group — "does not belong". The entity is untouched. */
  rejectMember: (caseId: string, body: GroupRejectMemberRequest) =>
    apiClient.post<void>(`/v1/cases/${caseId}/entity-resolution/groups/reject-member`, body),

  /** Drop a whole group — "none of this is a duplicate". Entities untouched. */
  dismissGroup: (caseId: string, body: GroupDismissRequest) =>
    apiClient.post<void>(`/v1/cases/${caseId}/entity-resolution/groups/dismiss`, body),

  /** Fold every fully-exact cluster with no unresolved identity conflicts
   * into its survivor. Returns a job document — poll {@link getMergeExactStatus}. */
  mergeExact: (caseId: string) =>
    apiClient.post<MergeExactJob>(`/v1/cases/${caseId}/entity-resolution/groups/merge-exact`),

  getMergeExactStatus: (caseId: string, jobId?: string) =>
    apiClient.get<MergeExactJob>(
      `/v1/cases/${caseId}/entity-resolution/groups/merge-exact/status`,
      {
        params: jobId ? { job_id: jobId } : undefined,
      }
    ),

  /** On-demand: scan same-label entities for duplicates on one property.
   * Returns a job document — poll {@link getFindDuplicatesStatus}. */
  findDuplicates: (caseId: string, body: FindDuplicatesRequest) =>
    apiClient.post<FindDuplicatesJob>(`/cases/${caseId}/entity-resolution/find-duplicates`, body),

  getFindDuplicatesStatus: (caseId: string, jobId?: string) =>
    apiClient.get<FindDuplicatesJob>(`/cases/${caseId}/entity-resolution/find-duplicates/status`, {
      params: jobId ? { job_id: jobId } : undefined,
    }),
}
